<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUsersStore } from '@/stores'
import { useAgentAdminStore } from '@/stores/agentAdmin.store'
import AgentChatMessage from '@/@core/components/AgentChatMessage.vue'

// RM-399 (10/21): the Text view shows each texted reply as the parts the member received;
// offered only when the conversation has texted messages.
const textView = ref(false)
import AgentTraceModal from '@/@core/components/AgentTraceModal.vue'
import AgentAnnotateDialog from '@/@core/components/AgentAnnotateDialog.vue'

const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const route = useRoute()
const store = useAgentAdminStore()

const searchValue = ref('')
const statusFilter = ref('')
// RM-205 member support: only conversations where RM AI asked for a person and nobody has answered.
const needsSupportOnly = ref(false)
const supportReplyText = ref('')
const supportReplyState = ref('') // '' | 'sending' | 'sent' | 'error'
const supportReplySentVia = ref('')
// RM-441: which member message the reply answers (the most recent by default), and exactly what will be sent.
const supportReplyTo = ref(null)
const supportPreview = ref(null)
const supportPreviewState = ref('') // '' | 'loading' | 'error'
const supportPreviewTimer = ref(null)
const currentPage = ref(1)
const searchDebounceTimer = ref(null)
const itemsPerPage = 25

// Trace modal state
const traceModalVisible = ref(false)
const traceConversationId = ref(null)
const traceMsgDbIndex = ref(null)

// RM-208 annotation dialog state
const annotateVisible = ref(false)
const annotateMsgDbIndex = ref(null)
const annotateAiText = ref('')

// RM-380: the open conversation's own card data (member profiles, restaurant cards), keyed the way
// the member's chat keys them. Without it every card in the conversation spun forever.
const conversationCardData = computed(() => {
  const conv = store.activeConversation
  if (!conv) return null
  const restaurantCards = {}
  for (const r of conv.restaurantCards || []) {
    if (r?.profileId) restaurantCards[r.profileId] = r
    if (r?._id) restaurantCards[r._id] = r
  }

  return { memberProfiles: conv.memberProfiles || {}, restaurantCards }
})

// Map annotated AI-message db index -> its most recent feedback record.
const feedbackByMsgIndex = computed(() => {
  const map = {}
  for (const f of store.activeFeedback || []) {
    // activeFeedback is newest-first, so the first seen per index is the latest.
    if (map[f.messageIndex] === undefined) map[f.messageIndex] = f
  }

  return map
})

// Member 👍/👎 per message (shown inline so reviewers see member sentiment).
const memberRatingByMsgIndex = computed(() => {
  const map = {}
  for (const f of store.activeFeedback || []) {
    if (f.source === 'member' && f.memberRating && map[f.messageIndex] === undefined) {
      map[f.messageIndex] = f.memberRating
    }
  }

  return map
})

// RM-316: members' RM AI conversations, or restaurant team people's texts with the RM team.
const viewMode = ref(route.query.view === 'restaurants' ? 'restaurants' : 'members')
const viewItems = [
  { title: 'Members', value: 'members' },
  { title: 'Restaurants', value: 'restaurants' },
]
const activeThreadPhone = ref(null)
const activeThread = computed(() => store.restaurantThreads.find(t => t.contactPhone === activeThreadPhone.value) || null)
const lastLine = thread => {
  const last = thread.messages[thread.messages.length - 1]
  if (!last) return ''
  
  return last.who === 'rm' ? `RM: ${last.text}` : last.text
}
const formatMessageTime = dateStr => (dateStr
  ? new Date(dateStr).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '')

const statusItems = [
  { title: 'Active', value: '' },
  { title: 'Archived', value: 'archived' },
  { title: 'All', value: 'all' },
]

const loadData = async () => {
  await store.getConversations({
    page: currentPage.value,
    limit: itemsPerPage,
    search: searchValue.value || undefined,
    status: statusFilter.value || undefined,
    support: needsSupportOnly.value ? 'open' : undefined,
  })
}

// RM-205 member support: send the reply the way a YES from a support contact's phone does.
// RM-441: it answers the member message picked above (the most recent by default).
const sendSupportReply = async () => {
  const text = supportReplyText.value.trim()
  if (!text || !store.activeConversation) return
  supportReplyState.value = 'sending'
  try {
    const res = await store.sendSupportReply(store.activeConversation.conversationId, text, supportReplyTo.value)
    supportReplySentVia.value = { text: 'text', email: 'email', group: 'the group text' }[res?.delivery] || 'the app'
    supportReplyText.value = ''
    supportPreview.value = null
    supportReplyTo.value = null
    supportReplyState.value = 'sent'
    setTimeout(() => { supportReplyState.value = '' }, 4000)
    loadData()
  } catch (error) {
    console.error('Support reply failed:', error)
    supportReplyState.value = 'error'
  }
}

// RM-441: the member messages an admin can answer, newest first for the picker; the most recent is the default.
const memberMessages = computed(() => (store.activeConversation?.messages || [])
  .filter(m => m.role === 'user' && m.msgDbIndex != null && !m.hidden)
  .map(m => ({ msgDbIndex: m.msgDbIndex, text: m.content, timestamp: m.timestamp }))
  .reverse())
const replyToOptions = computed(() => memberMessages.value.map((m, i) => {
  const when = i === 0 ? 'Latest' : formatDateTime(m.timestamp)
  const words = String(m.text || '').replace(/\s+/g, ' ')
  const short = words.length > 70 ? `${words.slice(0, 70)}…` : words

  return { value: m.msgDbIndex, title: `${when} - ${short}` }
}))
const answeredMessage = computed(() => memberMessages.value.find(m => m.msgDbIndex === supportReplyTo.value) || memberMessages.value[0] || null)
watch(memberMessages, list => {
  if (!list.some(m => m.msgDbIndex === supportReplyTo.value)) supportReplyTo.value = list[0]?.msgDbIndex ?? null
}, { immediate: true })

// Exactly what will be sent, from the server (the text's words, or the email's subject, sender, Bcc and body).
const refreshSupportPreview = async () => {
  const text = supportReplyText.value.trim()
  const conv = store.activeConversation
  if (!text || !conv) {
    supportPreview.value = null
    supportPreviewState.value = ''
    
    return
  }
  supportPreviewState.value = 'loading'
  try {
    const p = await store.previewSupportReply(conv.conversationId, text, supportReplyTo.value)
    if (store.activeConversation?.conversationId === conv.conversationId && supportReplyText.value.trim() === text) {
      supportPreview.value = p
      supportPreviewState.value = ''
    }
  } catch (error) {
    console.error('Support reply preview failed:', error)
    supportPreviewState.value = 'error'
  }
}
watch([supportReplyText, supportReplyTo], () => {
  clearTimeout(supportPreviewTimer.value)
  supportPreviewTimer.value = setTimeout(refreshSupportPreview, 350)
})
const supportPreviewLine = computed(() => {
  const p = supportPreview.value
  if (!p) return ''
  if (p.delivery === 'text') return `By text to ${p.to}`
  if (p.delivery === 'group') return 'Into the group text'
  if (p.delivery === 'email' && p.email) {
    const parts = [`By email to ${p.email.to}`]
    if (p.email.from) parts.push(`from ${p.email.from}`)
    if (p.email.cc?.length) parts.push(`cc ${p.email.cc.join(', ')}`)
    if (p.email.bcc?.length) parts.push(`bcc ${p.email.bcc.join(', ')}`)

    return parts.join(' · ')
  }
  
  return ''
})
const supportPreviewBody = computed(() => {
  const p = supportPreview.value
  if (!p) return ''
  if (p.delivery === 'email' && p.email) {
    const subject = p.email.subject ? `Subject: ${p.email.subject}\n\n` : ''

    return `${subject}${p.email.body || ''}`
  }
  
  return p.text || ''
})
const hasTextMessages = computed(() => (store.activeConversation?.messages || []).some(m => m.channel === 'sms'))

const supportReplyLabel = computed(() => ({
  sending: 'Sending…', sent: `Sent via ${supportReplySentVia.value}`, error: 'Send failed - try again',
}[supportReplyState.value] || 'Send as RM member support'))

const onSearchInput = () => {
  if (searchDebounceTimer.value) clearTimeout(searchDebounceTimer.value)
  searchDebounceTimer.value = setTimeout(() => {
    currentPage.value = 1
    loadData()
  }, 400)
}

const clearFilters = () => {
  searchValue.value = ''
  statusFilter.value = ''
  currentPage.value = 1
  loadData()
}

const onPageChange = newPage => {
  currentPage.value = newPage
  loadData()
}

const selectConversation = conversationId => {
  store.getConversation(conversationId)
}

const backToList = () => {
  store.clearActiveConversation()
}

const showTrace = msgDbIndex => {
  if (!store.activeConversation) return
  traceConversationId.value = store.activeConversation.conversationId
  traceMsgDbIndex.value = msgDbIndex
  traceModalVisible.value = true
}

const openAnnotate = msgDbIndex => {
  if (!store.activeConversation) return
  const msg = store.activeConversation.messages?.find(m => m.msgDbIndex === msgDbIndex)
  annotateMsgDbIndex.value = msgDbIndex
  annotateAiText.value = msg?.content || ''
  annotateVisible.value = true
}

// Copy / Download PDF (Kapil, 2026-09-16). Each action shows its own result in the button for a moment.
const copyState = ref('idle') // idle | working | done | error
const pdfState = ref('idle')
const flash = (state, value) => {
  state.value = value
  if (value === 'done' || value === 'error') setTimeout(() => { state.value = 'idle' }, 2500)
}

const copyConversation = async () => {
  const id = store.activeConversation?.conversationId
  if (!id || copyState.value === 'working') return
  flash(copyState, 'working')
  try {
    // Safari only allows a clipboard write inside the tap itself, so hand it the pending text as a
    // ClipboardItem promise; browsers without ClipboardItem fall back to writeText after the fetch.
    if (window.ClipboardItem && navigator.clipboard?.write) {
      const blob = store.fetchConversationText(id).then(text => new Blob([text], { type: 'text/plain' }))
      await navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': blob })])
    } else {
      const text = await store.fetchConversationText(id)
      await navigator.clipboard.writeText(text)
    }
    flash(copyState, 'done')
  } catch (error) {
    console.error('Copy conversation failed:', error)
    flash(copyState, 'error')
  }
}

const downloadPdf = async () => {
  const id = store.activeConversation?.conversationId
  if (!id || pdfState.value === 'working') return
  flash(pdfState, 'working')
  try {
    await store.downloadConversationPdf(id)
    flash(pdfState, 'done')
  } catch (error) {
    console.error('Download conversation PDF failed:', error)
    flash(pdfState, 'error')
  }
}

const copyLabel = computed(() => ({ working: 'Copying…', done: 'Copied', error: 'Copy failed' }[copyState.value] || 'Copy'))
const pdfLabel = computed(() => ({ working: 'Preparing…', done: 'Downloaded', error: 'Download failed' }[pdfState.value] || 'PDF'))

const formatDate = dateStr => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatDateTime = dateStr => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatRelativeTime = dateStr => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now - d
  const diffMin = Math.floor(diffMs / 60000)
  const diffHrs = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHrs < 24) return `${diffHrs}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return formatDate(dateStr)
}

const formatTokens = tokens => {
  if (!tokens) return '0'
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`
  
  return tokens.toString()
}

const userName = user => {
  if (!user) return 'Unknown'
  if (user.firstName || user.lastName) return `${user.firstName || ''} ${user.lastName || ''}`.trim()
  
  return user.email || 'Unknown'
}

watch(viewMode, mode => {
  activeThreadPhone.value = null
  if (mode === 'restaurants') store.getRestaurantThreads()
})

watch(needsSupportOnly, () => {
  currentPage.value = 1
  loadData()
})

watch(statusFilter, () => {
  currentPage.value = 1
  loadData()
})

onMounted(async () => {
  await Promise.all([loadData(), store.getStats(), viewMode.value === 'restaurants' ? store.getRestaurantThreads() : null])

  // RM-208: deep link from the AI Annotations page straight into a conversation.
  const deepLinkId = route.query.conversationId
  if (deepLinkId) store.getConversation(deepLinkId)
})
</script>

<template>
  <div>
    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n2"
    >
      <div class="back-button-container">
        <RouterLink
          to="/admin/profile"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Restaurant Admin</span>
        </RouterLink>
      </div>
    </div>

    <div class="grid-sidebar-left content-wrapper mt-6">
      <!-- Sidebar Filters -->
      <div>
        <VCard class="mb-4 pa-4 pt-8 pb-7">
          <div class="account-card">
            <div class="account-card-content">
              <div class="mb-2 font-weight-medium">
                Filters
              </div>

              <VSelect
                v-model="viewMode"
                class="mb-3"
                label="Show"
                item-title="title"
                item-value="value"
                :items="viewItems"
                density="compact"
              />

              <template v-if="viewMode === 'members'">
                <VSelect
                  v-model="statusFilter"
                  class="mb-3"
                  label="Status"
                  item-title="title"
                  item-value="value"
                  :items="statusItems"
                  density="compact"
                />

                <VSwitch
                  v-model="needsSupportOnly"
                  class="mb-3"
                  label="Needs member support"
                  color="warning"
                  density="compact"
                  hide-details
                />

                <VBtn
                  variant="tonal"
                  class="w-100"
                  @click="clearFilters"
                >
                  Clear Filters
                </VBtn>
              </template>
            </div>
          </div>
        </VCard>

        <VCard
          v-if="store.stats"
          class="pa-4"
        >
          <div class="text-caption text-medium-emphasis mb-2">
            Usage Summary
          </div>
          <div class="text-body-2 mb-1">
            <strong>{{ store.stats.totalConversations }}</strong> total conversations
          </div>
          <div class="text-body-2 mb-1">
            <strong>{{ store.stats.activeConversations }}</strong> active
          </div>
          <div class="text-body-2 mb-1">
            <strong>{{ store.stats.totalUsers }}</strong> unique users
          </div>
          <div class="text-body-2">
            <strong>{{ formatTokens(store.stats.totalTokens) }}</strong> total tokens
          </div>
        </VCard>
      </div>

      <!-- Main Content -->
      <div>
        <!-- RM-316: restaurant team people's texts with the RM team -->
        <VCard
          v-if="viewMode === 'restaurants' && !activeThread"
          class="pa-6 pt-5"
        >
          <VRow
            align="center"
            class="mb-2"
          >
            <VCol>
              <h3 class="user-dashboard-card-title mb-0">
                Restaurant texts
              </h3>
            </VCol>
            <VCol cols="auto">
              <VBtn
                icon="mdi-refresh"
                variant="text"
                color="grey"
                size="small"
                :loading="store.restaurantThreadsLoading"
                @click="store.getRestaurantThreads()"
              />
            </VCol>
          </VRow>
          <p class="text-body-2 text-medium-emphasis mb-4">
            Messages from restaurant team people and RM's replies. Reply from your phone to the RM team number.
          </p>

          <div
            v-if="store.restaurantThreadsLoading && store.restaurantThreads.length === 0"
            class="text-center py-8"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </div>
          <div
            v-else-if="store.restaurantThreads.length === 0"
            class="text-center py-8 text-medium-emphasis"
          >
            No restaurant texts yet
          </div>
          <div v-else>
            <div
              v-for="thread in store.restaurantThreads"
              :key="thread.contactPhone"
              class="conversation-row d-flex align-center pa-3 rounded cursor-pointer"
              @click="activeThreadPhone = thread.contactPhone"
            >
              <div class="flex-grow-1 overflow-hidden">
                <div class="d-flex align-center mb-1">
                  <span class="text-body-2 font-weight-medium text-truncate">
                    {{ thread.contactName }}
                  </span>
                  <span
                    v-if="thread.place"
                    class="text-caption text-medium-emphasis ml-2 text-no-wrap"
                  >
                    at {{ thread.place }}
                  </span>
                </div>
                <div class="d-flex align-center">
                  <span class="text-body-2 text-truncate conversation-title">
                    {{ lastLine(thread) }}
                  </span>
                  <VChip
                    v-if="thread.waiting"
                    size="x-small"
                    variant="tonal"
                    color="warning"
                    class="ml-2"
                  >
                    waiting on RM
                  </VChip>
                </div>
              </div>
              <div class="text-right ml-3 flex-shrink-0">
                <div class="text-caption text-medium-emphasis text-no-wrap">
                  {{ formatRelativeTime(thread.lastAt) }}
                </div>
              </div>
            </div>
          </div>
        </VCard>

        <VCard
          v-else-if="viewMode === 'restaurants'"
          class="pa-6 pt-5"
        >
          <div class="d-flex align-center mb-4">
            <VBtn
              icon="mdi-arrow-left"
              variant="text"
              color="grey"
              size="small"
              aria-label="Back to restaurant texts"
              @click="activeThreadPhone = null"
            />
            <div class="ml-2 overflow-hidden">
              <div class="text-body-1 font-weight-medium text-truncate">
                {{ activeThread.contactName }}<span v-if="activeThread.place"> at {{ activeThread.place }}</span>
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ activeThread.phoneDisplay }}
              </div>
            </div>
          </div>
          <div
            v-for="(message, i) in activeThread.messages"
            :key="i"
            class="restaurant-message"
            :class="{ 'restaurant-message--rm': message.who === 'rm' }"
          >
            <div class="text-caption text-medium-emphasis mb-1">
              {{ message.who === 'rm' ? `RM${message.by ? ` · ${message.by}` : ''}` : activeThread.contactName }} · {{ formatMessageTime(message.at) }}
            </div>
            <div class="text-body-1 restaurant-message__text">
              {{ message.text }}
            </div>
          </div>
        </VCard>

        <!-- Conversation List View -->
        <VCard
          v-else-if="!store.activeConversation"
          class="pa-6 pt-5"
        >
          <VRow
            align="center"
            class="mb-4"
          >
            <VCol>
              <h3 class="user-dashboard-card-title mb-0">
                AI Conversations
              </h3>
            </VCol>
            <VCol cols="auto">
              <VBtn
                icon="mdi-refresh"
                variant="text"
                color="grey"
                size="small"
                :loading="store.loading"
                @click="loadData"
              />
            </VCol>
          </VRow>

          <VTextField
            v-model="searchValue"
            :loading="store.loading"
            density="compact"
            variant="solo"
            label="Search by member name or email..."
            append-inner-icon="mdi-magnify"
            single-line
            hide-details
            class="mb-4"
            clearable
            @input="onSearchInput"
            @click:clear="() => { searchValue = ''; currentPage = 1; loadData() }"
          />

          <!-- Loading state -->
          <div
            v-if="store.loading && conversations.length === 0"
            class="text-center py-8"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </div>

          <!-- Empty state -->
          <div
            v-else-if="store.conversations.length === 0"
            class="text-center py-8 text-medium-emphasis"
          >
            No conversations found
          </div>

          <!-- Conversation list -->
          <div v-else>
            <div
              v-for="conv in store.conversations"
              :key="conv._id"
              class="conversation-row d-flex align-center pa-3 rounded cursor-pointer"
              @click="selectConversation(conv._id)"
            >
              <div class="flex-grow-1 overflow-hidden">
                <div class="d-flex align-center mb-1">
                  <span class="text-body-2 font-weight-medium text-truncate">
                    {{ userName(conv.user) }}
                  </span>
                  <span class="text-caption text-medium-emphasis ml-2 text-no-wrap">
                    {{ conv.user?.email }}
                  </span>
                </div>
                <div class="d-flex align-center">
                  <span class="text-body-2 text-truncate conversation-title">
                    {{ conv.title }}
                  </span>
                  <VChip
                    v-if="conv.status === 'archived'"
                    size="x-small"
                    variant="tonal"
                    color="grey"
                    class="ml-2"
                  >
                    archived
                  </VChip>
                  <VChip
                    v-if="conv.supportStatus === 'requested'"
                    size="x-small"
                    variant="tonal"
                    color="warning"
                    class="ml-2"
                  >
                    needs member support
                  </VChip>
                </div>
              </div>
              <div class="text-right ml-3 flex-shrink-0">
                <div class="text-caption text-medium-emphasis text-no-wrap">
                  {{ formatRelativeTime(conv.lastActivityAt) }}
                </div>
                <div class="d-flex align-center justify-end mt-1 gap-2">
                  <VChip
                    size="x-small"
                    variant="tonal"
                    color="primary"
                  >
                    {{ conv.messageCount?.user || 0 }} msg
                  </VChip>
                  <span class="text-caption text-medium-emphasis">
                    {{ formatTokens(conv.totalTokens) }} tok
                  </span>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div
              v-if="store.pages > 1"
              class="d-flex justify-center mt-4"
            >
              <VPagination
                :model-value="currentPage"
                :length="store.pages"
                :total-visible="5"
                density="compact"
                @update:model-value="onPageChange"
              />
            </div>
          </div>
        </VCard>

        <!-- Conversation Detail View -->
        <VCard
          v-else
          class="pa-6 pt-5"
        >
          <div class="conversation-header d-flex align-center flex-wrap mb-4">
            <VBtn
              icon="mdi-arrow-left"
              variant="text"
              color="grey"
              size="small"
              class="mr-2"
              @click="backToList"
            />
            <div class="flex-grow-1 overflow-hidden">
              <h3 class="user-dashboard-card-title mb-0 text-truncate">
                {{ store.activeConversation.title }}
              </h3>
              <div class="d-flex align-center flex-wrap mt-1">
                <span class="text-body-2 font-weight-medium">
                  {{ userName(store.activeConversation.user) }}
                </span>
                <span class="text-caption text-medium-emphasis ml-2">
                  {{ store.activeConversation.user?.email }}
                </span>
                <span class="text-caption text-medium-emphasis ml-2">
                  {{ formatDateTime(store.activeConversation.createdAt) }}
                </span>
                <VChip
                  v-if="store.activeConversation.totalTokens"
                  size="x-small"
                  variant="tonal"
                  color="grey"
                  class="ml-2"
                >
                  {{ formatTokens(store.activeConversation.totalTokens) }} tokens
                </VChip>
                <VChip
                  v-if="store.activeConversation.supportStatus === 'requested'"
                  size="x-small"
                  variant="tonal"
                  color="warning"
                  class="ml-2"
                >
                  needs member support
                </VChip>
              </div>
            </div>
            <!-- Transcript actions: secondary to reading the conversation, so quiet text buttons that name their result -->
            <div
              v-if="store.activeConversation.messages?.length"
              class="transcript-actions d-flex align-center flex-shrink-0 ml-3"
            >
              <VBtnToggle
                v-if="hasTextMessages"
                v-model="textView"
                density="compact"
                variant="outlined"
                divided
                mandatory
                class="mr-3"
                aria-label="Show replies as the app shows them, or as the texts the member received"
              >
                <VBtn
                  :value="false"
                  size="small"
                >
                  App
                </VBtn>
                <VBtn
                  :value="true"
                  size="small"
                >
                  Text
                </VBtn>
              </VBtnToggle>
              <VBtn
                variant="text"
                size="small"
                :color="copyState === 'error' ? 'error' : 'primary'"
                :prepend-icon="copyState === 'done' ? 'mdi-check' : 'mdi-content-copy'"
                aria-label="Copy conversation as text"
                @click="copyConversation"
              >
                {{ copyLabel }}
              </VBtn>
              <VBtn
                variant="text"
                size="small"
                :color="pdfState === 'error' ? 'error' : 'primary'"
                :prepend-icon="pdfState === 'done' ? 'mdi-check' : 'mdi-tray-arrow-down'"
                aria-label="Download conversation as PDF"
                @click="downloadPdf"
              >
                {{ pdfLabel }}
              </VBtn>
            </div>
          </div>

          <VDivider class="mb-4" />

          <!-- Loading state -->
          <div
            v-if="store.activeConversationLoading"
            class="text-center py-8"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </div>

          <!-- Messages -->
          <div
            v-else-if="store.activeConversation.messages?.length"
            class="conversation-messages"
          >
            <AgentChatMessage
              v-for="(msg, idx) in store.activeConversation.messages"
              :key="idx"
              :role="msg.role"
              :content="msg.content"
              :channel="msg.channel || 'web'"
              :card-data="conversationCardData"
              :actor="msg.actor || null"
              :support-reply="!!msg.supportReply"
              :sent="msg.sent || null"
              :text-view="textView"
              :is-admin="true"
              :msg-db-index="msg.msgDbIndex"
              :has-trace="msg.hasTrace"
              :annotated="msg.msgDbIndex != null && feedbackByMsgIndex[msg.msgDbIndex] != null"
              :annotation-status="feedbackByMsgIndex[msg.msgDbIndex]?.status"
              :member-rating="msg.msgDbIndex != null ? memberRatingByMsgIndex[msg.msgDbIndex] : null"
              :turn-trace="msg.turnTrace"
              :timestamp="msg.timestamp"
              @show-trace="showTrace"
              @annotate="openAnnotate"
            />
          </div>

          <div
            v-else
            class="text-center py-8 text-medium-emphasis"
          >
            No messages in this conversation
          </div>

          <!--
            RM-205 member support: one action - reply to the member as a person at RM.
            The member gets the words as written in their chat (labeled RM member support). By text if
            they're Active on RM via Text, otherwise by email (RM-413) - answering one of their messages
            inside the follow-up, shown below exactly as it will go out (RM-441).
          -->
          <div
            v-if="store.activeConversation.messages?.length && !store.activeConversationLoading"
            class="support-reply mt-6"
          >
            <div class="text-body-2 font-weight-medium mb-1">
              Reply as RM member support
            </div>
            <div class="text-caption text-medium-emphasis mb-2">
              Goes into this conversation, and to the member by text or email.
            </div>
            <VSelect
              v-if="replyToOptions.length > 1"
              v-model="supportReplyTo"
              :items="replyToOptions"
              label="Answering"
              density="comfortable"
              class="mb-2"
              hide-details
            />
            <div
              v-else-if="answeredMessage"
              class="text-caption text-medium-emphasis mb-2"
            >
              Answering: “{{ String(answeredMessage.text || '').replace(/\s+/g, ' ').slice(0, 120) }}”
            </div>
            <VTextarea
              v-model="supportReplyText"
              rows="3"
              auto-grow
              density="comfortable"
              placeholder="What to tell the member"
              :disabled="supportReplyState === 'sending'"
              hide-details
            />
            <div
              v-if="supportReplyText.trim()"
              class="support-preview mt-3"
            >
              <div class="text-caption text-medium-emphasis mb-1">
                <template v-if="supportPreviewState === 'loading' && !supportPreview">
                  Preparing the preview…
                </template>
                <template v-else-if="supportPreviewState === 'error'">
                  The preview could not be loaded - the reply can still be sent
                </template>
                <template v-else-if="supportPreviewLine">
                  {{ supportPreviewLine }}
                </template>
              </div>
              <pre
                v-if="supportPreviewBody"
                class="support-preview-body text-body-2"
              >{{ supportPreviewBody }}</pre>
            </div>
            <div class="d-flex justify-end mt-2">
              <VBtn
                :color="supportReplyState === 'error' ? 'error' : 'primary'"
                :variant="store.activeConversation.supportStatus === 'requested' ? 'flat' : 'tonal'"
                :loading="supportReplyState === 'sending'"
                :disabled="!supportReplyText.trim()"
                @click="sendSupportReply"
              >
                {{ supportReplyLabel }}
              </VBtn>
            </div>
          </div>
        </VCard>
      </div>
    </div>

    <!-- Trace Modal -->
    <AgentTraceModal
      v-model="traceModalVisible"
      :conversation-id="traceConversationId"
      :msg-db-index="traceMsgDbIndex"
    />

    <!-- RM-208 Annotation Dialog -->
    <AgentAnnotateDialog
      v-model="annotateVisible"
      :conversation-id="store.activeConversation?.conversationId"
      :msg-db-index="annotateMsgDbIndex"
      :ai-text="annotateAiText"
    />
  </div>
</template>

<style lang="scss" scoped>
.back-button-container {
  padding: 0 4px;
}

.back-button {
  padding: 8px 12px;
  color: rgb(51, 51, 51);
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 4px;
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: rgb(102, 102, 102);
}

.restaurant-message {
  max-inline-size: 85%;
  margin-block-end: 12px;
  padding-block: 12px;
  padding-inline: 16px;
  border-radius: 12px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.restaurant-message--rm {
  margin-inline-start: auto;
  background: rgba(var(--v-theme-primary), 0.08);
}

.restaurant-message__text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.conversation-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: background-color 0.15s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }

  &:last-child {
    border-bottom: none;
  }
}

.transcript-actions {
  gap: 4px;
}

.transcript-actions .v-btn {
  min-height: 40px;
}

/* Phones: the actions drop under the title instead of squeezing it */
@media (max-width: 600px) {
  .transcript-actions {
    width: 100%;
    margin-left: 40px !important;
    margin-top: 4px;
  }
}

.conversation-title {
  color: rgb(102, 102, 102);
}

.conversation-messages {
  max-height: 70vh;
  overflow-y: auto;
  padding: 8px 0;
}

/* RM-205 member support reply: inputs stay >=16px so iOS Safari doesn't zoom. */
.support-reply :deep(textarea),
.support-reply :deep(.v-field__input) {
  font-size: 16px;
}

/* RM-441: exactly what will be sent, as the member will read it - a quiet surface, the words as they are. */
.support-preview-body {
  margin: 0;
  padding: 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
